import { useCallback, useMemo, useRef, useState } from 'react'
import {
    Alert,
    AlertDescription,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    SimpleGrid,
    Spinner,
    Stack,
    IconButton,
    Tag,
    Text,
    Textarea,
    Tooltip,
    useClipboard,
    useDisclosure
} from '@chakra-ui/react'
import {
    FiAlertTriangle,
    FiCheck,
    FiCopy,
    FiUser,
    FiUsers,
    FiXCircle
} from 'react-icons/fi'
import { PartyMap } from '@/shared/api/hooks/useDuplicateParties'
import { useBranches } from '@/shared/api/hooks/useBranches'
import {
    LastCheckin,
    useChildLastCheckins
} from '@/shared/api/hooks/useChildLastCheckins'
import {
    DuplicateAction,
    ParentDuplicateRecord
} from '@/shared/api/rest/services/parent-duplicates.service'
import { ChildRecord } from '@/shared/api/rest/types'
import {
    DuplicateParty,
    DuplicateSide,
    KEEP_ACTION,
    SIDE_LABEL,
    STATUS_COLOR,
    STATUS_LABEL,
    duplicateStatus,
    formatDateTime,
    formatLastLogin,
    formatRelative,
    formatScore,
    getChildName,
    getPartyName,
    resolveParty,
    withBranchName
} from '../types'
import { useDuplicateActions } from '../hooks/useDuplicateActions'

type Props = {
    record: ParentDuplicateRecord | null
    parties: PartyMap
    /** Still hydrating names/children — the columns render placeholder text. */
    loading?: boolean
    /**
     * The pair lookup failed. Without this a throttled or 500-ing request looks
     * exactly like a record that genuinely has no details on it.
     */
    loadError?: Error | null
    /** The viewer's own parent id, so their own account is called out. */
    ownParentId?: string | null
    /** Shown in place of the comparison when no pair is selected. */
    emptyHint?: string
}

/**
 * The rows compared side by side, in the order they are shown.
 *
 * `needsFetch` marks the ones that only exist on `public.users` and so are not
 * in the duplicates payload — everything else renders from the embedded rows the
 * moment the list arrives, and must not be blanked out while the lookup is in
 * flight.
 */
const FIELDS: {
    key: string
    label: string
    needsFetch?: boolean
    /**
     * Skip the "these records disagree" flag. Two accounts were always going to
     * have been signed into at different times; marking that as a discrepancy
     * says nothing and blunts the flag where it does mean something.
     */
    skipCompare?: boolean
    /** Show a copy button next to the value. */
    copyable?: boolean
    value: (party?: DuplicateParty | null) => string
}[] = [
    {
        key: 'name',
        label: 'Name',
        needsFetch: true,
        value: (party) => getPartyName(party)
    },
    {
        key: 'email',
        label: 'Email',
        needsFetch: true,
        // Resolving a pair almost always means pasting the address somewhere
        // else — a search, a ticket, a message to the family. Two duplicates
        // differ by a dot or a digit, which is exactly what retyping by eye
        // gets wrong.
        copyable: true,
        value: (party) => party?.email?.trim() ?? ''
    },
    {
        key: 'phone',
        label: 'Phone',
        needsFetch: true,
        value: (party) => party?.phoneNumber?.trim() ?? ''
    },
    {
        key: 'relationship',
        label: 'Relationship',
        value: (party) => party?.relationship?.trim() ?? ''
    },
    {
        key: 'branch',
        label: 'Branch',
        value: (party) => party?.branch?.name?.trim() ?? ''
    },
    {
        key: 'address',
        label: 'Address',
        value: (party) =>
            [party?.address, party?.city, party?.state, party?.country]
                .map((part) => part?.trim())
                .filter(Boolean)
                .join(', ')
    },
    {
        key: 'created',
        label: 'Registered',
        value: (party) => formatDateTime(party?.createdAt)
    },
    {
        // The question behind the whole screen is which of two identical-looking
        // accounts the person actually uses; this is the field that answers it.
        key: 'lastLogin',
        label: 'Last login',
        skipCompare: true,
        value: (party) => formatLastLogin(party?.lastLoginAt)
    }
]

/**
 * One compared field: its label, the discrepancy flag, and the value.
 *
 * Its own component because the copy button needs `useClipboard`, which cannot
 * be called from inside the `FIELDS.map` that renders these.
 */
const FieldRow = ({
    label,
    value,
    differs,
    stalled,
    loading,
    copyable
}: {
    label: string
    value: string
    differs: boolean
    stalled: boolean
    loading: boolean
    copyable?: boolean
}) => {
    const { hasCopied, onCopy } = useClipboard(value)
    // Nothing to put on the clipboard while the lookup is still in flight, or
    // when the field is simply empty.
    const showCopy = Boolean(copyable) && Boolean(value) && !stalled

    return (
        <Box py={2} borderBottomWidth="1px" borderColor="gray.50">
            <Flex align="center" gap={1.5}>
                <Text fontSize="xs" textTransform="uppercase" color="gray.500">
                    {label}
                </Text>
                {differs ? (
                    <Tooltip label="These two records disagree on this field.">
                        <Box
                            as={FiAlertTriangle}
                            color="orange.400"
                            fontSize="xs"
                            aria-label="Differs between records"
                        />
                    </Tooltip>
                ) : null}
            </Flex>
            <Flex align="center" gap={1} minW={0}>
                <Text
                    fontSize="sm"
                    fontWeight={differs ? 'semibold' : 'normal'}
                    color={value && !stalled ? 'gray.800' : 'gray.400'}
                    // break-word, not break-all: a long email wraps rather
                    // than pushing the copy button off the column, but an
                    // address still breaks at spaces like ordinary prose.
                    wordBreak="break-word"
                    minW={0}
                >
                    {stalled ? (loading ? 'Loading…' : 'Unavailable') : value || '—'}
                </Text>
                {showCopy ? (
                    <Tooltip label={hasCopied ? 'Copied' : `Copy ${label.toLowerCase()}`}>
                        <IconButton
                            aria-label={`Copy ${label.toLowerCase()}`}
                            icon={hasCopied ? <FiCheck /> : <FiCopy />}
                            size="xs"
                            variant="ghost"
                            colorScheme={hasCopied ? 'green' : 'gray'}
                            onClick={onCopy}
                            flexShrink={0}
                        />
                    </Tooltip>
                ) : null}
            </Flex>
        </Box>
    )
}

/**
 * One identifier, in full and one click from the clipboard.
 *
 * Truncated ids are enough to tell two records apart on screen but useless for
 * following a row into the database or a log, which is the first thing anyone
 * does when a merge looks wrong — so both the parent id and the central user id
 * are shown whole.
 */
const IdRow = ({ label, value }: { label: string; value?: string | null }) => {
    const { hasCopied, onCopy } = useClipboard(value ?? '')

    return (
        <Flex align="center" gap={1} minW={0}>
            <Text
                fontSize="10px"
                textTransform="uppercase"
                color="gray.500"
                flexShrink={0}
                w="60px"
            >
                {label}
            </Text>
            <Text
                fontSize="11px"
                fontFamily="mono"
                color={value ? 'gray.600' : 'gray.400'}
                wordBreak="break-all"
                flex="1"
                minW={0}
            >
                {value || '—'}
            </Text>
            {value ? (
                <Tooltip label={hasCopied ? 'Copied' : `Copy ${label.toLowerCase()}`}>
                    <IconButton
                        aria-label={`Copy ${label.toLowerCase()}`}
                        icon={hasCopied ? <FiCheck /> : <FiCopy />}
                        size="xs"
                        variant="ghost"
                        colorScheme={hasCopied ? 'green' : 'gray'}
                        onClick={onCopy}
                        flexShrink={0}
                    />
                </Tooltip>
            ) : null}
        </Flex>
    )
}

/**
 * When this child was last checked in, in the space a list row can spare.
 *
 * The timestamp itself leads, because that is what tells the reviewer which of
 * two accounts is the one being used — with the elapsed time under it so a
 * two-year-old visit doesn't have to be worked out from the date. `undefined`
 * means the lookup hasn't landed; `null` means it came back and there is no
 * visit on record, which is a real answer and is shown rather than hidden.
 */
const LastCheckinCell = ({
    lastCheckin,
    loading
}: {
    lastCheckin?: LastCheckin
    loading: boolean
}) => {
    if (loading && lastCheckin === undefined) {
        return (
            <Text fontSize="10px" color="gray.400" flexShrink={0}>
                …
            </Text>
        )
    }
    if (lastCheckin === undefined) return null

    const stamp = lastCheckin?.checkedInDate ?? null

    if (!stamp) {
        return (
            <Tooltip label="This child has never been checked in.">
                <Text fontSize="xs" color="gray.400" flexShrink={0} textAlign="right">
                    Never
                </Text>
            </Tooltip>
        )
    }

    return (
        <Tooltip
            label={`Last checked in ${formatDateTime(stamp)}${
                lastCheckin?.checkedOut === false ? ' — still checked in' : ''
            }`}
        >
            <Box textAlign="right" flexShrink={0}>
                <Text fontSize="xs" color="gray.700" whiteSpace="nowrap">
                    {formatDateTime(stamp)}
                </Text>
                <Text fontSize="10px" color="gray.400" whiteSpace="nowrap">
                    {formatRelative(stamp)}
                </Text>
            </Box>
        </Tooltip>
    )
}

const ChildRow = ({
    child,
    shared,
    lastCheckin,
    loadingCheckin
}: {
    child: ChildRecord
    shared: boolean
    lastCheckin?: LastCheckin
    loadingCheckin: boolean
}) => (
    <Flex align="center" gap={2} py={1.5}>
        <Avatar size="xs" name={getChildName(child)} src={child.picture ?? undefined} />
        <Box minW={0} flex="1">
            <Text fontSize="sm" noOfLines={1}>
                {getChildName(child) || 'Unnamed child'}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {[
                    child.ageGroup,
                    typeof child.age === 'number' ? `${child.age} yrs` : null
                ]
                    .filter(Boolean)
                    .join(' · ') || 'No age group'}
            </Text>
        </Box>
        <LastCheckinCell lastCheckin={lastCheckin} loading={loadingCheckin} />
        {child.isCheckedIn ? (
            <Badge colorScheme="green" fontSize="10px" borderRadius="full" px={2}>
                In
            </Badge>
        ) : null}
        {shared ? (
            <Tooltip label="Already registered under both records — the merge leaves it untouched.">
                <Tag size="sm" colorScheme="gray" borderRadius="full">
                    Both
                </Tag>
            </Tooltip>
        ) : null}
    </Flex>
)

type SideProps = {
    side: DuplicateSide
    party?: DuplicateParty | null
    parentId: string
    /** Field keys whose value differs from the other side. */
    differing: Set<string>
    sharedChildIds: Set<string>
    isOwnAccount: boolean
    loading: boolean
    failed: boolean
    /** Absent when the pair has already been resolved. */
    onKeep?: () => void
    keepDisabled?: boolean
    /** Set once resolved: whether this is the record that survived. */
    outcome?: 'kept' | 'removed' | null
}

const SideColumn = ({
    side,
    party,
    parentId,
    differing,
    sharedChildIds,
    isOwnAccount,
    loading,
    failed,
    onKeep,
    keepDisabled,
    outcome
}: SideProps) => {
    const name = getPartyName(party)
    const children = useMemo(() => party?.children ?? [], [party])
    // Only the open pair's children, and only for staff who may read check-ins.
    const {
        lastCheckins,
        available: checkinsAvailable,
        loading: loadingCheckins
    } = useChildLastCheckins(children)
    // A merge deletes the losing parent row, so a resolved pair legitimately
    // points at an id that no longer resolves. Say so rather than showing an
    // empty card that reads like a loading failure — but only when the lookup
    // actually succeeded and came back empty.
    const missing = !loading && !failed && !party

    return (
        <Flex
            direction="column"
            borderWidth="1px"
            borderColor={outcome === 'kept' ? 'green.200' : 'gray.200'}
            bg={outcome === 'removed' ? 'gray.50' : 'white'}
            borderRadius="md"
            overflow="hidden"
            h="full"
        >
            <Box
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderColor="gray.100"
                bg={outcome === 'kept' ? 'green.50' : 'gray.50'}
            >
                <Flex align="center" gap={2} mb={1} flexWrap="wrap">
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        color="gray.500"
                    >
                        {SIDE_LABEL[side]}
                    </Text>
                    {isOwnAccount ? (
                        <Badge colorScheme="blue" borderRadius="full" px={2}>
                            You are signed in here
                        </Badge>
                    ) : null}
                    {outcome === 'kept' ? (
                        <Badge colorScheme="green" borderRadius="full" px={2}>
                            Kept
                        </Badge>
                    ) : null}
                    {outcome === 'removed' ? (
                        <Badge colorScheme="red" borderRadius="full" px={2}>
                            Removed
                        </Badge>
                    ) : null}
                </Flex>
                <Flex align="center" gap={3}>
                    <Avatar
                        size="md"
                        name={name || undefined}
                        icon={<FiUser />}
                        src={party?.picture ?? undefined}
                        flexShrink={0}
                    />
                    <Box minW={0} flex="1">
                        <Heading size="sm" noOfLines={1}>
                            {loading
                                ? 'Loading…'
                                : name ||
                                  (failed ? 'Name unavailable' : 'Unnamed parent')}
                        </Heading>
                        {/* `parentId` comes off the duplicate row itself, so it
                            is shown even when the lookup failed; `userId` needs
                            the fetched record. */}
                        <Stack spacing={0} mt={1}>
                            <IdRow label="Parent ID" value={parentId} />
                            <IdRow label="User ID" value={party?.userId} />
                        </Stack>
                    </Box>
                </Flex>
            </Box>

            {missing ? (
                <Box px={4} py={3}>
                    <Alert status="info" borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        <AlertDescription>
                            This record no longer exists — it was removed when the pair
                            was resolved.
                        </AlertDescription>
                    </Alert>
                </Box>
            ) : (
                <Box px={4} py={2}>
                    {FIELDS.map((field) => {
                        const value = field.value(party)
                        const differs = differing.has(field.key)
                        // Only the `public.users` fields are still in flight;
                        // the rest came with the duplicate row itself.
                        const stalled = Boolean(
                            field.needsFetch && (loading || (failed && !value))
                        )
                        return (
                            <FieldRow
                                key={field.key}
                                label={field.label}
                                value={value}
                                differs={differs}
                                stalled={stalled}
                                loading={loading}
                                copyable={field.copyable}
                            />
                        )
                    })}
                </Box>
            )}

            {/* Children are the point of the merge — a decision here moves them
                onto the surviving record, so both lists are shown in full. */}
            <Box px={4} pt={3} pb={2} flex="1">
                <Flex align="center" gap={2} mb={1}>
                    <Box as={FiUsers} color="gray.500" aria-hidden />
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        color="gray.500"
                    >
                        Children registered here
                    </Text>
                    <Badge borderRadius="full" px={2}>
                        {loading || (failed && children.length === 0)
                            ? '—'
                            : children.length}
                    </Badge>
                    {/* Heads the column of times on the right, so each row can
                        carry the value alone instead of repeating the label. */}
                    {checkinsAvailable && children.length > 0 ? (
                        <Text
                            ml="auto"
                            fontSize="10px"
                            textTransform="uppercase"
                            color="gray.400"
                        >
                            Last check-in
                        </Text>
                    ) : null}
                </Flex>
                {loading ? (
                    <Spinner size="xs" color="gray.400" />
                ) : children.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">
                        {missing
                            ? '—'
                            : failed
                              ? "Couldn't load the children for this record."
                              : 'No children registered under this record.'}
                    </Text>
                ) : (
                    <Stack spacing={0} divider={<Divider borderColor="gray.50" />}>
                        {children.map((child) => (
                            <ChildRow
                                key={child._id}
                                child={child}
                                shared={sharedChildIds.has(child._id)}
                                lastCheckin={lastCheckins[child._id]}
                                loadingCheckin={loadingCheckins}
                            />
                        ))}
                    </Stack>
                )}
            </Box>

            {onKeep ? (
                <Box px={4} py={3} borderTopWidth="1px" borderColor="gray.100">
                    <Button
                        w="full"
                        size="sm"
                        colorScheme="brand"
                        leftIcon={<FiCheck />}
                        onClick={onKeep}
                        isDisabled={keepDisabled}
                    >
                        {side === 'primary' ? 'Keep Primary' : 'Keep Secondary'}
                    </Button>
                </Box>
            ) : null}
        </Flex>
    )
}

type Confirm = {
    action: DuplicateAction
    title: string
    body: string
    confirmLabel: string
    colorScheme: string
}

export default function DuplicateCompare({
    record,
    parties,
    loading = false,
    loadError = null,
    ownParentId,
    emptyHint = 'Select a suspected duplicate to compare the two records.'
}: Props) {
    const { resolve, pending } = useDuplicateActions()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [confirm, setConfirm] = useState<Confirm | null>(null)
    const [notes, setNotes] = useState('')
    const cancelRef = useRef<HTMLButtonElement>(null)

    // Branch names are a separate cached lookup; the duplicate rows only carry
    // `branchId`.
    const { branches } = useBranches()
    const branchName = useCallback(
        (branchId?: string | null) =>
            branchId ? branches.find((b) => b.value === branchId)?.label ?? '' : '',
        [branches]
    )

    // The duplicates payload embeds both `parents` rows, so everything except
    // name/email/phone/children is already here. The fetched record layers on
    // top of that rather than replacing it, which is why a slow or failed
    // lookup still leaves a mostly-populated card.
    const primary = useMemo(
        () =>
            withBranchName(
                resolveParty(record?.parent, record ? parties[record.parentId] : null),
                branchName
            ),
        [record, parties, branchName]
    )
    const secondary = useMemo(
        () =>
            withBranchName(
                resolveParty(
                    record?.duplicate,
                    record ? parties[record.duplicateId] : null
                ),
                branchName
            ),
        [record, parties, branchName]
    )

    const differing = useMemo(() => {
        const keys = new Set<string>()
        if (!record) return keys
        for (const field of FIELDS) {
            if (field.skipCompare) continue
            // A field still being fetched reads as equal on both sides ('' vs
            // ''), which would wrongly clear a difference flag — skip it until
            // the values are actually known.
            if (field.needsFetch && loading) continue
            if (field.value(primary) !== field.value(secondary)) keys.add(field.key)
        }
        return keys
    }, [record, loading, primary, secondary])

    const sharedChildIds = useMemo(() => {
        const primaryIds = new Set((primary?.children ?? []).map((child) => child._id))
        return new Set(
            (secondary?.children ?? [])
                .map((child) => child._id)
                .filter((id) => primaryIds.has(id))
        )
    }, [primary, secondary])

    if (!record) {
        return (
            <Flex justify="center" align="center" h="full" minH="240px" p={6}>
                <Text color="gray.500" textAlign="center">
                    {emptyHint}
                </Text>
            </Flex>
        )
    }

    const status = duplicateStatus(record)
    // Never offer a merge on top of a failed lookup: both columns would be
    // blank, and picking between two blanks is how the wrong record gets
    // deleted. Retry first.
    const isPending = status === 'pending' && !loadError
    const primaryName = getPartyName(primary) || 'the primary record'
    const secondaryName = getPartyName(secondary) || 'the secondary record'
    const primaryChildCount = primary?.children?.length ?? 0
    const secondaryChildCount = secondary?.children?.length ?? 0

    /** What "keep this side" actually does, spelled out before it happens. */
    const keepBody = (side: DuplicateSide) => {
        const keepName = side === 'primary' ? primaryName : secondaryName
        const dropName = side === 'primary' ? secondaryName : primaryName
        const movingCount = side === 'primary' ? secondaryChildCount : primaryChildCount
        const childPhrase =
            movingCount === 0
                ? 'No children need to move.'
                : `${movingCount} ${
                      movingCount === 1 ? 'child' : 'children'
                  } will move onto the record you keep.`
        // The backend deletes the losing `parents` row outright. If that row is
        // the viewer's own, they keep their login but lose the parent profile
        // behind it — their children now hang off the other account. Say so
        // before they click, not after.
        const dropId = side === 'primary' ? record.duplicateId : record.parentId
        const losingOwnRecord = !!ownParentId && ownParentId === dropId
        const ownWarning = losingOwnRecord
            ? ' This is the account you are currently signed in to: after the merge you will need to sign in with the other account to see your children.'
            : ''
        return `Keep ${keepName} (${SIDE_LABEL[
            side
        ].toLowerCase()}) and merge the other account into it. ${childPhrase} Check-in history and co-parent links transfer too, then ${dropName} is deleted. This cannot be undone.${ownWarning}`
    }

    const ask = (next: Confirm) => {
        setConfirm(next)
        onOpen()
    }

    const askKeep = (side: DuplicateSide) =>
        ask({
            action: KEEP_ACTION[side],
            title: side === 'primary' ? 'Keep primary record' : 'Keep secondary record',
            body: keepBody(side),
            confirmLabel: side === 'primary' ? 'Keep Primary' : 'Keep Secondary',
            colorScheme: 'brand'
        })

    const askReject = () =>
        ask({
            action: 'reject',
            title: 'Not a duplicate',
            body: 'Mark these two records as separate people. Both accounts are left exactly as they are and this pair stops being offered for review.',
            confirmLabel: 'Not a duplicate',
            colorScheme: 'gray'
        })

    const runConfirm = async () => {
        if (!confirm) return
        try {
            await resolve({ id: record._id, action: confirm.action, notes })
            setNotes('')
        } catch {
            // useDuplicateActions surfaces the failure as an error toast.
        }
        onClose()
    }

    // Which side survived, once resolved. `accepted` is true for both merge
    // directions, so the direction itself comes from which record is now gone.
    const outcomeFor = (side: DuplicateSide): 'kept' | 'removed' | null => {
        if (status !== 'merged' || loading) return null
        const party = side === 'primary' ? primary : secondary
        const other = side === 'primary' ? secondary : primary
        if (party && !other) return 'kept'
        if (!party && other) return 'removed'
        return null
    }

    return (
        <Box p={{ base: 3, md: 5 }}>
            <Flex align="center" gap={3} flexWrap="wrap" mb={1}>
                <Heading size="md">Suspected duplicate</Heading>
                <Badge
                    colorScheme={STATUS_COLOR[status]}
                    borderRadius="full"
                    px={3}
                    py={1}
                >
                    {STATUS_LABEL[status]}
                </Badge>
                <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                    {formatScore(record.similarityScore)} match
                </Badge>
            </Flex>
            <Text color="gray.600" fontSize="sm">
                {record.duplicateReason || 'Flagged by duplicate detection'}
                {formatDateTime(record.createdAt)
                    ? ` · detected ${formatDateTime(record.createdAt)}`
                    : ''}
            </Text>
            {/* The `parent_duplicates` row id — the handle for this pair in the
                database, distinct from either parent's id. */}
            <Box maxW="sm" mt={1}>
                <IdRow label="Pair ID" value={record._id} />
            </Box>

            {loadError ? (
                <Alert status="error" borderRadius="md" mt={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        Couldn&apos;t load names, emails or the children under each record
                        ({loadError.message}). Everything below came from the duplicate
                        record itself. The merge actions stay hidden until the rest loads
                        — a merge moves children between these accounts, so it
                        shouldn&apos;t be decided without seeing them. Reload to retry.
                    </AlertDescription>
                </Alert>
            ) : isPending ? (
                <Alert status="warning" borderRadius="md" mt={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        These two accounts look like the same person. Keep whichever
                        record is correct — the other is merged into it and deleted.
                    </AlertDescription>
                </Alert>
            ) : (
                <Alert status="info" borderRadius="md" mt={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        Reviewed {formatDateTime(record.processedAt) || 'earlier'}
                        {status === 'merged'
                            ? ' — the two accounts were merged.'
                            : ' — marked as two separate people.'}
                        {record.notes ? ` Note: ${record.notes}` : ''}
                    </AlertDescription>
                </Alert>
            )}

            <SimpleGrid
                columns={{ base: 1, lg: 2 }}
                spacing={4}
                mt={4}
                alignItems="stretch"
            >
                <SideColumn
                    side="primary"
                    party={primary}
                    parentId={record.parentId}
                    differing={differing}
                    sharedChildIds={sharedChildIds}
                    isOwnAccount={!!ownParentId && ownParentId === record.parentId}
                    loading={loading}
                    failed={!!loadError}
                    onKeep={isPending ? () => askKeep('primary') : undefined}
                    keepDisabled={pending}
                    outcome={outcomeFor('primary')}
                />
                <SideColumn
                    side="secondary"
                    party={secondary}
                    parentId={record.duplicateId}
                    differing={differing}
                    sharedChildIds={sharedChildIds}
                    isOwnAccount={!!ownParentId && ownParentId === record.duplicateId}
                    loading={loading}
                    failed={!!loadError}
                    onKeep={isPending ? () => askKeep('secondary') : undefined}
                    keepDisabled={pending}
                    outcome={outcomeFor('secondary')}
                />
            </SimpleGrid>

            {isPending ? (
                <Box mt={5} pt={4} borderTopWidth="1px" borderColor="gray.100">
                    <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
                        Note (optional)
                    </Text>
                    <Textarea
                        size="sm"
                        rows={2}
                        maxW="2xl"
                        placeholder="Why did you decide this? Saved with the record."
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                    />
                    <Flex mt={3} align="center" gap={3} flexWrap="wrap">
                        <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<FiXCircle />}
                            onClick={askReject}
                            isDisabled={pending}
                        >
                            Not a duplicate
                        </Button>
                        <Text fontSize="xs" color="gray.500">
                            Leaves both accounts untouched.
                        </Text>
                    </Flex>
                </Box>
            ) : null}

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {confirm?.title}
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">{confirm?.body}</AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button
                                ref={cancelRef}
                                size="sm"
                                variant="ghost"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                colorScheme={confirm?.colorScheme}
                                isLoading={pending}
                                onClick={runConfirm}
                            >
                                {confirm?.confirmLabel}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    )
}
